import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Smartphone } from 'lucide-react';
import { AppBrandLogo } from './AppBrandLogo';
import { usePwaInstallOffer } from '../../hooks/usePwaInstallOffer';

interface PwaInstallInlinePanelProps {
  className?: string;
}

export const PwaInstallInlinePanel: React.FC<PwaInstallInlinePanelProps> = ({ className = '' }) => {
  const { t } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');
  const { installed, manualKind, canNativeInstall, showNonChromeInstallHint, isInstalling, promptNativeInstall } =
    usePwaInstallOffer();

  const helpText = installed
    ? t('pushInstallOpenApp', {
        defaultValue: 'Installed. Open the app from your home screen, then allow notifications there.',
      })
    : manualKind === 'ios'
      ? tCommon('pwa.installHelpIos')
      : manualKind === 'mac'
        ? tCommon('pwa.installHelpMac')
        : manualKind === 'generic'
          ? window.isSecureContext
            ? tCommon('pwa.installHelpGenericSecure')
            : tCommon('pwa.installHelpGenericInsecure')
          : t('pushInstallLead', {
              defaultValue: 'Add the app, then open it and allow notifications to get alerts on your phone.',
            });

  return (
    <div
      className={`overflow-hidden rounded-xl border border-amber-200/80 bg-gradient-to-br from-amber-50/95 via-white to-orange-50/70 px-3 py-2.5 shadow-sm dark:border-amber-800/55 dark:from-amber-950/40 dark:via-gray-900/80 dark:to-orange-950/30 ${className}`}
      role="region"
      aria-label={t('pushInstallTitle', { defaultValue: 'Install the app' })}
    >
      <div className="flex items-start gap-2.5">
        <AppBrandLogo className="h-10 w-10 shrink-0 sm:h-11 sm:w-11" alt="" />
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100">
            {installed ? (
              <Smartphone className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
            ) : (
              <Download className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
            )}
            {t('pushInstallTitle', { defaultValue: 'Install the app' })}
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-slate-600 dark:text-slate-400">{helpText}</p>
        </div>
      </div>
      {!installed && showNonChromeInstallHint && (
        <p className="mt-2 w-full rounded-md border border-amber-300/80 bg-amber-50/90 px-2 py-1.5 text-[11px] leading-relaxed text-amber-950 dark:border-amber-700/70 dark:bg-amber-950/45 dark:text-amber-100">
          {tCommon('pwa.installSecurityHint', {
            defaultValue:
              'This browser may show an Android security warning during install. You can confirm it to finish, or install from Chrome to avoid that warning.',
          })}
        </p>
      )}

      {!installed && canNativeInstall && (
        <div className="mt-2.5 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => void promptNativeInstall()}
            disabled={isInstalling}
            className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isInstalling ? (
              tCommon('pwa.opening')
            ) : (
              <>
                <span className="sm:hidden">{tCommon('pwa.addToHomeScreen')}</span>
                <span className="hidden sm:inline">{tCommon('pwa.addToDesktop')}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
